import React, { createContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { AppRoutes } from './pages/Routes';
import '@inmoment/the-kitchen/themes/xi.css';
import '@inmoment/the-kitchen/style.css';
import { TopBar } from './cookbook-ui/custom-components/TopBar/TopBar';
import { SnackBar, _LottiesOptions, SuggestionObject, SnackBarProps } from '@inmoment/the-kitchen';
import { pages } from './pages';
import { TextAccordion } from './cookbook-ui/custom-components/TextAccordion/TextAccordion';
import { allComponents } from './automated-nav/allComponents';
import { otherSearchablePages } from './utils/copy';
import { CacheBuster } from './utils/CacheBuster';
import { ErrorBoundary } from './utils/ErrorBoundary';
import { _AppIconMeta, _IconMeta, _SVGMeta } from './cookbook-ui/visual-components/automated-components/graphics/metaExports';
import { kitchenVersion } from './utils/version';
import { useFullStory } from './utils/useScript';
import { useUserInfo } from './pages/hiddenPages/userInfo/UpdateUserInfo';
import { Metric, useMetrics } from './useMetrics';
import { BreakingChange, useBreakingChanges } from './useBreakingChanges';
import { useReleasesWithTickets, ReleaseWithTickets } from './useVersionUpdates';

const graphics = [..._SVGMeta, ..._AppIconMeta, ..._IconMeta].map((graphic) => {
  let type = 'Icon';
  let category = 'Icons';
  if (graphic.tags.includes('svg')) {
    type = 'SVG';
    category = 'Graphics';
  } else if (graphic.tags.includes('appicon')) {
    type = 'AppIcon';
  }
  return { text: `${graphic.name}.svg - ${category} – ${type}`, id: type };
});
const components = allComponents.map((comp) => ({ text: `${comp.name} – ${comp.category} – Component`, id: 'Component' }));
const animations = _LottiesOptions.map((animation) => ({ text: `${animation} - Graphics - XIAnimation`, id: 'Animation' }));
const allCombinedTags = [...graphics, ...components, ...animations, ...otherSearchablePages];

// these are hardcoded because the server was built by labs - treat as a 3rd party
// const patternParentUrl = `${process.env.REACT_APP_BACKEND}/confluencePageChildren?pageId=${process.env.REACT_APP_PATTERN_PARENT_PAGE}`;

type Theme = 'CX' | 'admin' | 'MX' | 'EX' | 'orca';
interface ThemeContext {
  theme: Theme;
  updateTheme(color: Theme): void;
}
export const KitchenThemeContext = createContext<ThemeContext>({ theme: 'CX', updateTheme: () => {} });

if (typeof theKitchen !== 'undefined') {
  theKitchen.isEnabled = true;
}

interface ContextProps {
  versionNumber: string;
  componentMetrics: Metric[];
  breakingChanges: BreakingChange[];
  patternIdPages: any[];
  updates: ReleaseWithTickets[];
  isLoadingBreakingChanges: boolean;
  isLoadingUpdates: boolean;
  allCombinedTags: SuggestionObject[];
}
export const KitchenContext = createContext<Partial<ContextProps>>({});

interface PatternId {
  title: string;
  id: string;
}

const App: React.FC = () => {
  const [snackbarObj /* _setSnackBarObj */] = React.useState<SnackBarProps | undefined>();
  const [patternIdPagesState /*, setPatternIdPagesState */] = React.useState<PatternId[]>([]);
  const [theme, setTheme] = React.useState<'CX' | 'admin' | 'MX' | 'EX' | 'orca'>('CX');

  const location = useLocation();
  const navigate = useNavigate();
  useFullStory();
  useUserInfo();

  const metrics = useMetrics();
  const { breakingChanges, status: breakingChangesStatus /* error: _breakingChangesError */ } = useBreakingChanges();
  const { releasesWithTickets: versionUpdates, status: releasesStatus /* error: _releasesError */ } = useReleasesWithTickets();

  const { patternSearchState, pagesState } = React.useMemo(() => {
    const containsIds: string[] = [];
    const pagesInSearch: SuggestionObject[] = [];
    const formattedPatternIds = patternIdPagesState
      ? patternIdPagesState.map((patternId) => {
          containsIds.push(patternId.id);
          pagesInSearch.push({ text: `${patternId.title} – Pattern - Page`, id: patternId.id });
          return {
            title: patternId.title,
            href: `/Pattern/${patternId.id}`,
            sections: [{ title: patternId.title, id: patternId.id }],
          };
        })
      : [];

    const result = pages.map((page) => {
      if (page.title === 'Patterns') {
        page.items = formattedPatternIds as any;
        page.containsIds = containsIds;
      }
      return page;
    });
    return {
      patternSearchState: pagesInSearch,
      pagesState: result,
    };
  }, [patternIdPagesState]);

  const themeContext = React.useMemo<ThemeContext>(
    () => ({
      theme: theme,
      updateTheme: setTheme,
    }),
    [theme],
  );

  const contextProps = React.useMemo<ContextProps>(
    () => ({
      componentMetrics: metrics,
      breakingChanges: breakingChanges,
      versionNumber: kitchenVersion,
      patternIdPages: patternIdPagesState,
      allCombinedTags: [...allCombinedTags, ...patternSearchState],
      updates: versionUpdates,
      isLoadingBreakingChanges: breakingChangesStatus === 'loading',
      isLoadingUpdates: releasesStatus === 'loading',
    }),
    [metrics, breakingChanges, patternIdPagesState, patternSearchState, versionUpdates, breakingChangesStatus, releasesStatus],
  );

  React.useEffect(() => {
    const lastBit = window.location.href.split('/').slice(-1)[0];
    if (lastBit.split('#').length > 1) {
      navigate(`/${lastBit}`, { replace: true });
    }
  }, [navigate]);

  // fetching patterns
  // React.useEffect(() => {
  //   axios
  //     .get(patternParentUrl)
  //     .then((res) => {
  //       setSnackBarObj(patternLoadingSuccess);
  //       setPatternIdPagesState(res.data);
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //       setSnackBarObj(errorObj('Patterns'));
  //     });
  // }, []);

  React.useEffect(() => {
    const _window = window as any;
    if (!_window.env) {
      _window.env = {};
    }
    if (!_window.env.urls) {
      _window.env.urls = {};
    }
    _window.env.urls.resources = process.env.REACT_APP_ICON_ASSETS;
  }, []);

  return (
    <ErrorBoundary>
      <CacheBuster
        renderer={({
          loading,
          isLatestVersion,
          refreshCacheAndReload,
        }: {
          loading: boolean;
          isLatestVersion: boolean;
          refreshCacheAndReload: () => void;
        }) => {
          if (loading) return null;
          if (!loading && !isLatestVersion) {
            // You can decide how and when you want to force reload
            refreshCacheAndReload();
          }
          return (
            <KitchenContext.Provider value={contextProps}>
              <KitchenThemeContext.Provider value={themeContext}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <TopBar />
                  <div
                    style={{
                      display: 'flex',
                      flex: 1,
                      height: 'calc(100% - 66px)',
                    }}
                  >
                    {location.pathname !== '/UserInfo' && (
                      <div
                        style={{
                          width: 282,
                          flexDirection: 'row',
                          overflow: 'auto',
                          background: 'white',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                          }}
                        >
                          <TextAccordion
                            color={theme}
                            data={pagesState}
                            onItemClick={(item) => {
                              if (item.title) {
                                if ((item.href as string).split('#').length > 1) {
                                  navigate(item.href, { replace: true });
                                } else {
                                  navigate(item.href);
                                }
                                const id = item.title.replace(/[ ]/gim, '-');
                                const d = document.getElementById(id);
                                d?.scrollIntoView();
                              } else {
                                if ((item.href as string).split('#').length > 1) {
                                  navigate(item.href, { replace: true });
                                } else {
                                  navigate(item.href);
                                }
                                const d = document.getElementById(item.href.substring(item.href.indexOf('#') + 1, item.href.length));
                                d?.scrollIntoView();
                              }
                            }}
                            location={location.pathname}
                          />
                        </div>
                      </div>
                    )}
                    <div id="route-wrapper">{AppRoutes}</div>
                  </div>
                </div>
                <SnackBar {...snackbarObj} />
              </KitchenThemeContext.Provider>
            </KitchenContext.Provider>
          );
        }}
      />
    </ErrorBoundary>
  );
};

export default App;
